/**
 * Pure helpers behind the recorder's live microphone meter. Kept dependency
 * free (no AudioContext, no DOM) so the RMS math and the silence detector
 * are trivial to unit test — the whole point of the "silent recording"
 * incident was that nothing on screen proved audio was actually arriving.
 */

/** Root-mean-square of time-domain audio samples in the AnalyserNode's
 * native range (-1..1). Returns 0 for an empty buffer. */
export function computeRms(samples: ArrayLike<number>): number {
  if (samples.length === 0) return 0;
  let sumSquares = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const value = samples[i]!;
    sumSquares += value * value;
  }
  return Math.sqrt(sumSquares / samples.length);
}

/**
 * Normalises a raw RMS reading into a 0-1 meter level. RMS computed from
 * -1..1 samples already sits in that range short of clipping; this just
 * clamps and guards against NaN/negative input so the bar and the silence
 * check always see a sane number.
 */
export function rmsToLevel(rms: number): number {
  if (!Number.isFinite(rms) || rms <= 0) return 0;
  return Math.min(1, rms);
}

/** Below this normalised level the microphone is treated as silent. */
export const SILENCE_LEVEL_THRESHOLD = 0.005;
/** How long the level must stay below threshold, straight, before we warn. */
export const SILENCE_DURATION_MS = 10_000;

export type SilenceEvent = 'warning' | 'cleared' | null;

/**
 * Tracks whether the microphone has been silent for `durationMs` straight.
 * Every sample is fed in explicitly with its own timestamp, so this has no
 * hidden timers of its own and is deterministic under fake timers in tests.
 */
export class SilenceDetector {
  private silenceStartedAt: number | null = null;
  private warned = false;

  constructor(
    private readonly thresholdLevel: number = SILENCE_LEVEL_THRESHOLD,
    private readonly durationMs: number = SILENCE_DURATION_MS,
  ) {}

  /**
   * Call once per meter sample. Returns 'warning' the instant the silent
   * stretch crosses `durationMs` (fired once per stretch), 'cleared' the
   * instant sound returns after a warning was shown, or null otherwise.
   */
  update(level: number, nowMs: number): SilenceEvent {
    if (level < this.thresholdLevel) {
      if (this.silenceStartedAt === null) this.silenceStartedAt = nowMs;
      if (!this.warned && nowMs - this.silenceStartedAt >= this.durationMs) {
        this.warned = true;
        return 'warning';
      }
      return null;
    }
    this.silenceStartedAt = null;
    if (this.warned) {
      this.warned = false;
      return 'cleared';
    }
    return null;
  }

  /** Used when a fresh silent stretch should not inherit an old timer. */
  reset(): void {
    this.silenceStartedAt = null;
    this.warned = false;
  }
}
